import React, { useState, useEffect } from "react";
import { func, object, bool, string, number, oneOfType } from "prop-types";
import { register, login } from "@quintype/bridgekeeper-js";
import { useDispatch } from "react-redux";
import { MEMBER_UPDATED } from "../../store/actions";
import CloseIcon from "../../atoms/CloseIcon";
import styles from "../../story-templates/shared/disclaimer-modal.m.css";

const GuestBuyReportModal = ({ isOpen, onClose, selectedPlanId, initRazorPayPayment, story, onSignInClick }) => {
  const [userInfo, setUserInfo] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    designation: "",
    password: "",
    agreed: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorMsg, setErrorMsg] = useState("");
  const dispatch = useDispatch();

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("modal-open");
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUserInfo((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (errorMsg) setErrorMsg("");
  };

  const validate = () => {
    const newErrors = {};
    if (!String(userInfo.name || "").trim()) newErrors.name = "Name is required";
    if (!String(userInfo.email || "").trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userInfo.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!String(userInfo.phone || "").trim()) newErrors.phone = "Phone is required";
    if (!String(userInfo.company || "").trim()) newErrors.company = "Company is required";
    if (!String(userInfo.designation || "").trim()) newErrors.designation = "Designation is required";
    if (!String(userInfo.password || "").trim()) {
      newErrors.password = "Password is required";
    } else if (userInfo.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }
    if (!userInfo.agreed) newErrors.agreed = "You must agree to proceed";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegisterAndBuy = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!validate()) return;

    setIsLoading(true);
    setErrorMsg("");

    const userObj = {
      name: userInfo.name,
      email: userInfo.email,
      username: userInfo.email,
      password: userInfo.password,
      metadata: {
        "phone-number": userInfo.phone,
        organisation: userInfo.company,
        designation: userInfo.designation,
      }
    };

    try {
      // 1. Register the user
      const { user, message } = await register(userObj);
      
      if (!user && message === "User Already exists") {
        // Try logging them in if they already exist with this password
        try {
          const loginResp = await login(userInfo.email, userInfo.password);
          if (loginResp?.user) {
            dispatch({ type: MEMBER_UPDATED, member: loginResp.user });
            await proceedToPayment();
            return;
          }
        } catch (loginErr) {
          setErrorMsg("User already exists. Please sign in instead.");
          setIsLoading(false);
          return;
        }
      }

      if (user) {
        dispatch({ type: MEMBER_UPDATED, member: user });
        
        // Send email notification immediately after successful sign up
        try {
          await fetch('/buy-report-email', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              name: userInfo.name,
              email: userInfo.email,
              phone: userInfo.phone,
              company: userInfo.company,
              designation: userInfo.designation,
              reportTitle: story?.headline,
              reportId: story?.id,
            }),
          });
        } catch (emailErr) {
          console.error("Failed to send buy report notification immediately", emailErr);
        }

        await proceedToPayment();
      } else {
        setErrorMsg(message || "Failed to register. Please try again.");
        setIsLoading(false);
      }
    } catch (err) {
      console.error(err);
      if (err.status === 409) {
        setErrorMsg(`The email '${userObj.email}' already exists. Please sign in.`);
      } else {
        setErrorMsg(err.message || "Oops! Something went wrong");
      }
      setIsLoading(false);
    }
  };

  const proceedToPayment = async () => {
    try {
      if (initRazorPayPayment) {
        await initRazorPayPayment(selectedPlanId, story.id);
        window.location.reload();
      }
    } catch (err) {
      console.error(err);
      if (err?.error?.code === "subscription_preview_active_subscription") {
        setErrorMsg("You already have an active subscription for this asset.");
      } else {
        setErrorMsg(err.message || "Payment failed. Please try again.");
      }
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const inputClass = (name) => {
    return errors[name] ? `${styles.input} ${styles.inputError}` : styles.input;
  };

  const isFormFilled = 
    String(userInfo.name || "").trim() !== "" &&
    String(userInfo.email || "").trim() !== "" &&
    String(userInfo.phone || "").trim() !== "" &&
    String(userInfo.company || "").trim() !== "" &&
    String(userInfo.designation || "").trim() !== "" &&
    String(userInfo.password || "").trim() !== "";

  const handleSignInOpen = () => {
    onClose();
    if (onSignInClick) {
      onSignInClick();
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.closeBtn} onClick={onClose}>
          <CloseIcon size={12} strokeWidth={1.25} color="var(--Icon-icon-primary, #242424)" />
        </div>
        
        <h2 className={styles.title}>Enter your details</h2>
        
        <form onSubmit={handleRegisterAndBuy} className={styles.form}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              name="name"
              placeholder="Name*"
              value={userInfo.name}
              onChange={handleChange}
              className={inputClass("name")}
            />
          </div>

          <div className={styles.inputGroup}>
            <div className={`${styles.field} ${styles.fieldLong}`}>
              <input
                type="email"
                name="email"
                placeholder="Email*"
                value={userInfo.email}
                onChange={handleChange}
                className={inputClass("email")}
              />
            </div>
            <div className={`${styles.field} ${styles.fieldShort}`}>
              <input
                type="text"
                name="phone"
                placeholder="Mobile*"
                value={userInfo.phone}
                onChange={handleChange}
                className={inputClass("phone")}
              />
            </div>
          </div>

          <div className={styles.inputGroup}>
            <div className={`${styles.field} ${styles.fieldLong}`}>
              <input
                type="text"
                name="company"
                placeholder="Company*"
                value={userInfo.company}
                onChange={handleChange}
                className={inputClass("company")}
              />
            </div>
            <div className={`${styles.field} ${styles.fieldShort}`}>
              <input
                type="text"
                name="designation"
                placeholder="Designation*"
                value={userInfo.designation}
                onChange={handleChange}
                className={inputClass("designation")}
              />
            </div>
          </div>

          <div style={{ position: 'relative' }}>
            <input
              type="password"
              name="password"
              placeholder="Password*"
              value={userInfo.password}
              onChange={handleChange}
              className={inputClass("password")}
            />
          </div>

          <div className={styles.checkboxContainer}>
            <input
              type="checkbox"
              id="agreed"
              name="agreed"
              checked={userInfo.agreed}
              onChange={handleChange}
              className={styles.checkbox}
              disabled={!isFormFilled}
            />
            <label htmlFor="agreed" className={isFormFilled ? styles.label : `${styles.label} ${styles.labelDisabled}`}>
              I agree that IMA India may share my contact information with its relevant partner
            </label>
          </div>

          {errorMsg && <p style={{ color: "#e53935", fontSize: "14px", margin: "16px 0 0 0" }}>{errorMsg}</p>}

          <button 
            type="submit" 
            disabled={isLoading || !isFormFilled || !userInfo.agreed}
            className={styles.submitBtn}
          >
            {isLoading ? "Processing..." : "Sign In & Buy Report"}
          </button>

          <p className={styles.footer}>
            <span className={styles.signIn} onClick={handleSignInOpen}>Sign in</span> if you are already a member
          </p>
        </form>
      </div>
    </div>
  );
};

GuestBuyReportModal.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  selectedPlanId: oneOfType([string, number]),
  initRazorPayPayment: func,
  story: object,
  onSignInClick: func,
};

export default GuestBuyReportModal;
